import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
/**
 * @deprecated Este componente está deprecated. Use <VideoSelectorV2> de @/components/shared/VideoSelectorV2
 * Para manter compatibilidade com wizards, este componente continua funcionando mas não receberá atualizações.
 */

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useVideoDashboardStore } from '@/store/videoDashboard';
import { Video } from '@/store/videoDashboard/types';
import { Search, Upload, Clock, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { VideoUploadInline } from './VideoUploadInline';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from '@/hooks/use-toast';

interface VideoSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectVideo: (video: { id: string; title: string; thumbnailUrl: string; duration: number }) => void;
}

export function VideoSelectionDialog({ open, onOpenChange, onSelectVideo }: VideoSelectionDialogProps) {
  useLanguage();
  const { videos } = useVideoDashboardStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  // Filter videos: only show processed videos
  const filteredVideos = useMemo(() => {
    return videos
      .filter(v => v.status === 'processed')
      .filter(v => {
        if (!searchQuery) return true;
        return v.title.toLowerCase().includes(searchQuery.toLowerCase());
      });
  }, [videos, searchQuery]);

  const handleConfirm = () => {
    if (!selectedVideoId) return;
    
    const video = videos.find(v => v.id === selectedVideoId);
    if (!video) return;

    onSelectVideo({
      id: video.id,
      title: video.title,
      thumbnailUrl: video.thumbnailUrl,
      duration: video.duration,
    });

    toast({
      title: translateInterfaceText("Vídeo selecionado"),
      description: video.title,
      duration: 2000,
    });
  };

  const handleVideoUploadComplete = (videoId: string) => {
    setSelectedVideoId(videoId);
    setShowUpload(false);
    toast({
      title: translateInterfaceText("Upload concluído"),
      description: translateInterfaceText("Vídeo disponível na biblioteca"),
      duration: 2000,
    });
  };

  // Format duration from seconds to mm:ss
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{translateInterfaceText("Selecionar Vídeo")}</DialogTitle>
        </DialogHeader>

        {/* Search and Upload */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={translateInterfaceText("Buscar vídeos...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Collapsible open={showUpload} onOpenChange={setShowUpload}>
            <CollapsibleTrigger asChild>
              <Button variant="outline">
                <Upload className="w-4 h-4 mr-2" />
                {translateInterfaceText("Upload Novo Vídeo")}</Button>
            </CollapsibleTrigger>
          </Collapsible>
        </div>

        {/* Upload Section (Collapsible) */}
        <Collapsible open={showUpload} onOpenChange={setShowUpload}>
          <CollapsibleContent>
            <div className="border rounded-lg p-4 mb-4">
              <VideoUploadInline onUploadComplete={handleVideoUploadComplete} />
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Video Grid */}
        <div className="flex-1 overflow-y-auto">
          {filteredVideos.length === 0 ? (
            <div className="text-center py-12">
              <Upload className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">
                {searchQuery ? translateInterfaceText("Nenhum vídeo encontrado") : translateInterfaceText("Você ainda não tem vídeos")}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery 
                  ? translateInterfaceText("Nenhum vídeo encontrado para \"{value0}\"", { value0: (searchQuery) })
                  : translateInterfaceText("Faça upload do primeiro vídeo para começar")}
              </p>
              <Button onClick={() => setShowUpload(true)}>
                {translateInterfaceText("Upload Novo Vídeo")}</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredVideos.map((video) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  isSelected={selectedVideoId === video.id}
                  onSelect={() => setSelectedVideoId(video.id)}
                  formatDuration={formatDuration}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {translateInterfaceText("Cancelar")}</Button>
          <Button onClick={handleConfirm} disabled={!selectedVideoId}>
            {translateInterfaceText("Confirmar Seleção")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Video Card Component
function VideoCard({
  video,
  isSelected,
  onSelect,
  formatDuration,
}: {
  video: Video;
  isSelected: boolean;
  onSelect: () => void;
  formatDuration: (seconds: number) => string;
}) {
  useLanguage();
  return (
    <button
      onClick={onSelect}
      className={`relative group text-left rounded-lg border-2 overflow-hidden transition-all hover:scale-[1.02] hover:shadow-lg ${
        isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-transparent'
      }`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-muted">
        <img
          src={video.thumbnailUrl}
          alt={video.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-2 right-2 bg-black/80 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatDuration(video.duration)}
        </div>
        {video.status === 'processing' && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Badge variant="secondary">{translateInterfaceText("Processando...")}</Badge>
          </div>
        )}
        {isSelected && (
          <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
            <Check className="w-4 h-4" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3">
        <h4 className="font-medium truncate">{video.title}</h4>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="secondary" className="text-xs">
            {translateInterfaceText("Host VSL")}</Badge>
        </div>
      </div>
    </button>
  );
}
